import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Stack,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import Tooltip from "@mui/material/Tooltip";
import axios from "../../../config/axiosConfig";
import { useAuth } from "../../../context/AuthContext";
import CentroEquipoDetalleDialog from "../../requerimiento/components/CentroEquipoDetalleDialog";

// Estado 3 (Asignado) de una OT de Mantenimiento: espejo de finalizar_mantenimiento.php
// + falla.php/cargaDetalle.php/guardaFalla.php del sistema viejo. Cada equipo que entró
// a Mantenimiento necesita un diagnóstico (checklist de fallas + observación) antes de
// poder "Finalizar Diagnóstico" — opcionalmente, en el mismo diagnóstico se puede
// reemplazar el equipo por uno Inactivo de la misma sucursal (hot-swap: el cliente no
// se queda sin servicio mientras la unidad vieja va al taller).
const headCellSx = {
  fontWeight: 800,
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};
const tableSx = { "& .MuiTableCell-root": { py: 0.25, px: 0.6, fontSize: 11 } };
const controlSx = {
  "& .MuiInputLabel-root": { fontSize: 11 },
  "& .MuiOutlinedInput-root": { height: 30, fontSize: 11.5 },
};

const FAMILIA_LABEL = { RADIO: "Radios", BATERIA: "Baterías", VARIOS: "Equipos", CHIP: "Chips" };
const ESTADOS_FINALES = ["Mantenimiento", "Inactivo", "Baja"];
const PAGE_SIZE_REEMPLAZOS = 8;

const historialEndpoint = (familia, identificador) => {
  if (familia === "RADIO") return `/api/historial-radio/serie/${encodeURIComponent(identificador)}`;
  if (familia === "BATERIA") return `/api/historial-bateria/serie/${encodeURIComponent(identificador)}`;
  return null;
};

function HistorialDialog({ open, familia, identificador, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const url = historialEndpoint(familia, identificador);
    if (!url) return;
    setLoading(true);
    axios.get(url).then((res) => setRows(res.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, [open, familia, identificador]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Historial de {identificador}</DialogTitle>
      <DialogContent dividers sx={{ p: 1 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={20} /></Box>
        ) : (
          <TableContainer>
            <Table size="small" sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>Estado</TableCell>
                  <TableCell sx={headCellSx}>Operación</TableCell>
                  <TableCell sx={headCellSx}>Fecha</TableCell>
                  <TableCell sx={headCellSx}>Observación</TableCell>
                  <TableCell sx={headCellSx}>Usuario</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id_historial} sx={r.operacion === "Mantenimiento" ? { bgcolor: "warning.light" } : undefined}>
                    <TableCell>{r.estado}</TableCell>
                    <TableCell>{r.operacion}</TableCell>
                    <TableCell>{String(r.fecha || "").slice(0, 10)}</TableCell>
                    <TableCell>{r.observacion}</TableCell>
                    <TableCell>{r.usu}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

// Buscador + tabla paginada de equipos Inactivo disponibles para reemplazo (en vez de
// un <Select> con todo el listado de una), igual de patrón que las tablas de
// selección de equipo del módulo (SeleccionarMantenimientoOT / DevolucionEquiposOT).
function ReemplazoPicker({ familia, sucursal, tipoEquipoVarios, value, onSelect }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [buscar, setBuscar] = useState("");
  const [buscarAplicado, setBuscarAplicado] = useState("");
  const [loading, setLoading] = useState(true);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE_REEMPLAZOS));

  const cargar = useCallback((nextPage = 1, buscarTexto = "") => {
    setLoading(true);
    axios.get("/api/ot-mantenimiento/reemplazos", {
      params: { familia, sucursal, tipoEquipoVarios, buscar: buscarTexto, page: nextPage, pageSize: PAGE_SIZE_REEMPLAZOS },
    })
      .then((res) => {
        const { items: rows = [], total: totalRows = 0, page: p = nextPage } = res.data || {};
        setItems(rows);
        setTotal(Number(totalRows));
        setPage(Number(p));
      })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [familia, sucursal, tipoEquipoVarios]);

  useEffect(() => {
    setBuscar("");
    setBuscarAplicado("");
    cargar(1, "");
  }, [cargar]);

  const handleBuscar = () => {
    setBuscarAplicado(buscar.trim());
    cargar(1, buscar.trim());
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <TextField
          size="small"
          label="Buscar equipo de reemplazo"
          value={buscar}
          onChange={(e) => setBuscar(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleBuscar(); }}
          sx={{ minWidth: 220, ...controlSx }}
        />
        <IconButton size="small" onClick={handleBuscar} title="Buscar">
          <SearchIcon fontSize="small" />
        </IconButton>
        <Chip size="small" label={`Total: ${total}`} sx={{ height: 20, fontSize: 11 }} />
        {value && (
          <Chip size="small" color="success" label={`Elegido: ${value}`} onDelete={() => onSelect("")} sx={{ height: 22, fontSize: 11 }} />
        )}
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 220, borderRadius: 0.5 }}>
        <Table size="small" stickyHeader sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Identificador</TableCell>
              <TableCell sx={headCellSx}>Modelo</TableCell>
              <TableCell sx={{ ...headCellSx, width: 90 }}>Acción</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 1.5 }}><CircularProgress size={16} /></TableCell>
              </TableRow>
            ) : !items.length ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    No hay equipos Inactivo disponibles en esta sucursal.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((r) => {
                const val = r.serie ?? r.numero;
                const seleccionado = value === val;
                return (
                  <TableRow key={val} hover selected={seleccionado}>
                    <TableCell>{val}</TableCell>
                    <TableCell>{r.modelo}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant={seleccionado ? "contained" : "outlined"}
                        color={seleccionado ? "success" : "primary"}
                        onClick={() => onSelect(seleccionado ? "" : val)}
                        sx={{ minHeight: 22, height: 22, fontSize: 10.5 }}
                      >
                        {seleccionado ? "Elegido" : "Elegir"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center" sx={{ mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          Página {total ? page : 0} / {total ? totalPages : 0}
        </Typography>
        <Button size="small" variant="outlined" startIcon={<NavigateBeforeIcon />} disabled={loading || page <= 1}
          onClick={() => cargar(page - 1, buscarAplicado)} sx={{ minHeight: 24, height: 24, fontSize: 10.5 }}>
          Anterior
        </Button>
        <Button size="small" variant="outlined" endIcon={<NavigateNextIcon />} disabled={loading || page >= totalPages}
          onClick={() => cargar(page + 1, buscarAplicado)} sx={{ minHeight: 24, height: 24, fontSize: 10.5 }}>
          Siguiente
        </Button>
      </Stack>
    </Box>
  );
}

// Panel de diagnóstico: se despliega DEBAJO de la fila del equipo (en vez de un
// Dialog flotante), para no tapar el resto de la tabla mientras se completa.
function DiagnosticoInlinePanel({ item, cod_ot, cod_cliente, cod_servicio_cliente, sucursal, permitirReemplazo = true, onCancel, onSaved }) {
  const [fallas, setFallas] = useState([]);
  const [fallaSel, setFallaSel] = useState([]);
  const [obs, setObs] = useState("");
  const [reemplazar, setReemplazar] = useState(false);
  const [serieReemplazo, setSerieReemplazo] = useState("");
  const [estadoFinal, setEstadoFinal] = useState("Mantenimiento");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const tipoParaFalla = item?.familia === "VARIOS" ? item?.tipoEquipoVarios : item?.familia;
  const obsError = submitted && !obs.trim();

  useEffect(() => {
    if (!item) return;
    setFallaSel([]);
    setObs("");
    setReemplazar(false);
    setSerieReemplazo("");
    setEstadoFinal("Mantenimiento");
    setError("");
    setSubmitted(false);
    axios.get(`/api/ot-mantenimiento/fallas/${encodeURIComponent(tipoParaFalla || "")}`)
      .then((res) => setFallas(res.data || []))
      .catch(() => setFallas([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.familia, item?.identificador]);

  const toggleFalla = (id) => {
    setFallaSel((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const guardar = async () => {
    setSubmitted(true);
    if (!obs.trim()) {
      setError("La observación es obligatoria.");
      return;
    }
    if (reemplazar && !item?.yaReemplazado && !serieReemplazo) {
      setError("Elige un equipo de reemplazo en la tabla, o desmarca la opción de reemplazar.");
      return;
    }
    setGuardando(true);
    setError("");
    try {
      await axios.post("/api/ot-mantenimiento/diagnostico", {
        cod_ot,
        familia: item.familia,
        identificador: item.identificador,
        obs,
        fallaIds: fallaSel,
        serieReemplazo: reemplazar ? serieReemplazo : undefined,
        estadoFinal: reemplazar ? estadoFinal : undefined,
        cod_cliente,
        cod_servicio_cliente,
      });
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo guardar el diagnóstico.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Box sx={{ p: 1.5, bgcolor: (t) => (t.palette.mode === "dark" ? "background.default" : "grey.50") }}>
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        Fallas detectadas (opcional)
      </Typography>
      {!fallas.length ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontStyle: "italic" }}>
          No hay fallas catalogadas para este tipo de equipo.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 2, mb: 1.5 }}>
          {fallas.map((f) => (
            <FormControlLabel
              key={f.id_falla}
              control={<Checkbox size="small" checked={fallaSel.includes(f.id_falla)} onChange={() => toggleFalla(f.id_falla)} />}
              label={<Typography variant="body2">{f.descripcion}</Typography>}
            />
          ))}
        </Box>
      )}

      <TextField
        label="Observación"
        required
        fullWidth
        multiline
        rows={2}
        value={obs}
        onChange={(e) => setObs(e.target.value)}
        error={obsError}
        helperText={obsError ? "La observación es obligatoria." : ""}
        sx={{ mb: 1.5, bgcolor: "background.paper" }}
      />

      {!permitirReemplazo ? (
        <Alert severity="info" sx={{ mt: 0.5 }}>
          En el mantenimiento de equipos vendidos no se reemplazan equipos: aquí solo se registran las observaciones del diagnóstico.
        </Alert>
      ) : item?.yaReemplazado ? (
        <Alert severity="info" sx={{ mt: 0.5 }}>
          Este equipo ya fue reemplazado y quedó fuera del cliente — no se puede volver a reemplazar.
        </Alert>
      ) : (
        <FormControlLabel
          control={<Checkbox checked={reemplazar} onChange={(e) => setReemplazar(e.target.checked)} />}
          label="Reemplazar por un equipo Inactivo de esta sucursal"
        />
      )}

      {permitirReemplazo && reemplazar && !item?.yaReemplazado && (
        <Box sx={{ mt: 0.5 }}>
          <ReemplazoPicker
            familia={item.familia}
            sucursal={sucursal}
            tipoEquipoVarios={item.tipoEquipoVarios}
            value={serieReemplazo}
            onSelect={setSerieReemplazo}
          />
          <FormControl size="small" sx={{ minWidth: 200, mt: 1 }}>
            <InputLabel id="estado-final-label">Estado final unidad vieja</InputLabel>
            <Select
              labelId="estado-final-label"
              label="Estado final unidad vieja"
              value={estadoFinal}
              onChange={(e) => setEstadoFinal(e.target.value)}
            >
              {ESTADOS_FINALES.map((e) => (
                <MenuItem key={e} value={e}>{e}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button onClick={onCancel} disabled={guardando}>Cancelar</Button>
        <Button variant="contained" onClick={guardar} disabled={guardando}>
          {guardando ? <CircularProgress size={18} /> : "Guardar diagnóstico"}
        </Button>
      </Stack>
    </Box>
  );
}

const DiagnosticoMantenimientoOT = ({ cod_ot, cod_cliente, cod_servicio_cliente, esTKR, permitirReemplazo = true, onFinalizado }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [historialItem, setHistorialItem] = useState(null);
  const [equipoDialog, setEquipoDialog] = useState(null);
  const [finalizando, setFinalizando] = useState(false);
  const panelRef = React.useRef(null);

  const abrirDiagnostico = (row) => {
    setSelectedItem(row);
    // El panel vive al final de la página (debajo de todas las tablas), lejos de la
    // fila que se clickeó — sin este scroll el usuario no se da cuenta de que se abrió.
    requestAnimationFrame(() => panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  const cargar = useCallback(() => {
    setLoading(true);
    setError("");
    axios.get(`/api/ot-mantenimiento/diagnostico/${cod_ot}`)
      .then((res) => setItems(res.data || []))
      .catch((err) => setError(err?.response?.data?.error || "No se pudo cargar el diagnóstico."))
      .finally(() => setLoading(false));
  }, [cod_ot]);

  useEffect(() => { cargar(); }, [cargar]);

  const pendientes = useMemo(() => items.filter((i) => i.pendiente), [items]);
  const puedeFinalizar = !loading && items.length > 0 && pendientes.length === 0;

  const grupos = esTKR
    ? [["RADIO", items.filter((i) => i.familia === "RADIO")], ["BATERIA", items.filter((i) => i.familia === "BATERIA")]]
    : [["VARIOS", items.filter((i) => i.familia === "VARIOS")], ["CHIP", items.filter((i) => i.familia === "CHIP")]];

  const handleFinalizar = async () => {
    if (!window.confirm('¿Finalizar el diagnóstico? La OT pasará a "Procesando".')) return;
    setFinalizando(true);
    try {
      const { data } = await axios.patch(`/api/ot-mantenimiento/finalizar-diagnostico/${cod_ot}`);
      if (onFinalizado) onFinalizado(data);
    } catch (err) {
      alert(err?.response?.data?.error || "No se pudo finalizar el diagnóstico.");
    } finally {
      setFinalizando(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={22} /></Box>;
  }
  if (error) {
    return <Alert severity="error" action={<Button size="small" onClick={cargar}>Reintentar</Button>}>{error}</Alert>;
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
        <Chip size="small" label={`Total: ${items.length}`} variant="outlined" />
        <Chip size="small" color="warning" label={`Pendientes: ${pendientes.length}`} />
        <Chip size="small" color="success" label={`Diagnosticados: ${items.length - pendientes.length}`} />
      </Stack>

      {grupos.map(([familia, rows]) => (
        rows.length > 0 && (
          <Paper key={familia} variant="outlined" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, p: 1 }}>{FAMILIA_LABEL[familia]}</Typography>
            <TableContainer>
              <Table size="small" sx={tableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Identificador</TableCell>
                    <TableCell sx={headCellSx}>Modelo</TableCell>
                    <TableCell sx={headCellSx}>Observación</TableCell>
                    <TableCell sx={headCellSx}>Estado</TableCell>
                    {esTKR && <TableCell sx={headCellSx}>Mant. últ. 3 meses</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const rowKey = `${row.familia}-${row.identificador}`;
                    const pendiente = row.pendiente;
                    const seleccionada = selectedItem && `${selectedItem.familia}-${selectedItem.identificador}` === rowKey;
                    return (
                      <TableRow key={rowKey} hover selected={seleccionada}>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {row.esReemplazoNuevo && (
                              <Tooltip title="Entró como reemplazo de mantenimiento">
                                <ArrowUpwardIcon sx={{ fontSize: 15, color: "success.main" }} />
                              </Tooltip>
                            )}
                            {row.yaReemplazado && (
                              <Tooltip title="Salió, reemplazado por otro equipo">
                                <ArrowDownwardIcon sx={{ fontSize: 15, color: "error.main" }} />
                              </Tooltip>
                            )}
                            <Box
                              component="span"
                              onClick={() => setEquipoDialog({ tipo: familia.toLowerCase(), identificador: row.identificador })}
                              sx={{ cursor: "pointer", color: "primary.main", textDecoration: "underline" }}
                            >
                              {row.identificador}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell
                          onClick={() => pendiente && abrirDiagnostico(row)}
                          sx={pendiente ? {
                            cursor: "pointer",
                            bgcolor: "error.light",
                            color: "error.contrastText",
                            fontWeight: 700,
                            textDecoration: "underline",
                          } : undefined}
                          title={pendiente ? "Adicionar diagnóstico" : ""}
                        >
                          {row.modelo}
                        </TableCell>
                        <TableCell>{row.observacion}</TableCell>
                        <TableCell>
                          <Chip size="small" color={pendiente ? "warning" : "success"} label={pendiente ? "Pendiente" : "OK"} sx={{ height: 20, fontSize: 10.5 }} />
                        </TableCell>
                        {esTKR && (
                          <TableCell>
                            <Button size="small" variant="outlined" onClick={() => setHistorialItem(row)} sx={{ minHeight: 22, height: 22, fontSize: 10.5 }}>
                              {row.mantenimientosUltimos3Meses}
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )
      ))}

      {!items.length && (
        <Alert severity="info">No hay equipos en Mantenimiento para esta OT.</Alert>
      )}

      {selectedItem && (
        <Paper ref={panelRef} variant="outlined" sx={{ mb: 2, borderColor: "primary.main" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, p: 1 }}>
            Diagnóstico — {selectedItem.identificador} ({selectedItem.modelo})
          </Typography>
          <DiagnosticoInlinePanel
            item={selectedItem}
            cod_ot={cod_ot}
            cod_cliente={cod_cliente}
            cod_servicio_cliente={cod_servicio_cliente}
            sucursal={user?.sucursal}
            permitirReemplazo={permitirReemplazo}
            onCancel={() => setSelectedItem(null)}
            onSaved={() => { setSelectedItem(null); cargar(); }}
          />
        </Paper>
      )}

      {puedeFinalizar && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant="contained" color="success" onClick={handleFinalizar} disabled={finalizando}>
            {finalizando ? <CircularProgress size={20} color="inherit" /> : "Finalizar Diagnóstico"}
          </Button>
        </Box>
      )}

      <HistorialDialog
        open={!!historialItem}
        familia={historialItem?.familia}
        identificador={historialItem?.identificador}
        onClose={() => setHistorialItem(null)}
      />

      <CentroEquipoDetalleDialog
        open={!!equipoDialog}
        tipo={equipoDialog?.tipo}
        identificador={equipoDialog?.identificador}
        onClose={() => setEquipoDialog(null)}
      />
    </Box>
  );
};

export default DiagnosticoMantenimientoOT;
